import type {FunctionComponent} from "react";
import type {IconName} from "@cdx/ds";
import {DSButton, Row} from "@cdx/ds";
import dsStyles from "@cdx/ds/css/index.css";
import type {UserRole} from "@cdx/shared/auth/scopes";
import {api} from "../../lib/api";
import cdxEnv from "../../env";
import {getRoleInclProducer} from "../../lib/auth/roles";
import {waitForResultPromise as rawWaitForResultPromise} from "../../lib/wait-for-result";
import {checkIfPresent} from "../../lib/mate/mate-utils";
import type {Root} from "../../cdx-models/Root";
import type {AccountOnboardingStep} from "../../cdx-models/AccountOnboardingStep";

const waitForResultPromise = rawWaitForResultPromise as <T>(fn: () => T) => Promise<T>;

export const isOnboardingAvailable = () => cdxEnv.ON_PREMISE !== "true";

export type ChapterName =
  | "basics1"
  | "basics2"
  | "basics3"
  | "hand"
  | "textEditor"
  | "lifeCycle"
  | "advanced"
  | "advanced2"
  | "moreCardTypes"
  | "journeys"
  | "sorting"
  | "timeTracking"
  | "producer"
  | "discord"
  | "openDecks"
  | "bonus";

export type StepInfo<Payload = undefined> = {
  startedAt: string;
  completedAt: string | null;
  payload: Payload;
};
export type StepProgress = {[key: string]: StepInfo};

export const ONBOARDING_STEPS = {
  openDeck: "openDeck",
  switchToHandTab: "switchToHandTab",
  investigateDonePile: "investigateDonePile",
  inviteTeamMember: "inviteTeamMember",
  cardSelection: "cardSelection",
  goToCard: "goToCard",
  dragAndDropCard: "dragAndDropCard",
  visitTimeTrackingReport: "visitTimeTrackingReport",
  applyDefaultOrder: "applyDefaultOrder",
  investigateUpvoteStats: "investigateUpvoteStats",
  investigateUserSettings: "investigateUserSettings",
  investigateMetrics: "investigateMetrics",
  investigateActivityFeed: "investigateActivityFeed",
  investigateOrgSettings: "investigateOrgSettings",
  investigateTeamHands: "investigateTeamHands",
  investigateCardBacklink: "investigateCardBacklink",
  bulkEditPriority: "bulkEditPriority",
  searchForPriority: "searchForPriority",
  showAssignedCards: "showAssignedCards",
  useSorting: "useSorting",
  filterBySwimlaneHeader: "filterBySwimlaneHeader",
  joinCodecksDiscord: "joinCodecksDiscord",
  investigateShortcutPanel: "investigateShortcutPanel",
  addSubCard: "addSubCard",
  applySCMIntegration: "applySCMIntegration",
} as const;

type FrontendKey = keyof typeof ONBOARDING_STEPS;

export type OnboardingInfo = {
  stepProgress: StepProgress;
  availableSteps: AccountOnboardingStep[];
  role: UserRole | "producer" | null;
};

export const getAvailableAccountOnboardingInfo = (root: Root): OnboardingInfo => {
  const accountSteps = root.account.accountOnboarding?.$meta.get(
    "steps",
    null
  ) as StepProgress | null;
  const rawUserSteps = root.loggedInUser?.userOnboarding
    ? root.loggedInUser.userOnboarding.$meta.get("steps", null)
    : (false as StepProgress | null | false);
  const userSteps = (rawUserSteps === false ? {} : rawUserSteps) as StepProgress | null;
  const steps = accountSteps && userSteps ? {...accountSteps, ...userSteps} : null;
  const variants = root.account.accountOnboarding?.$meta.get("variants", null);
  const staffQuestMode = root.account.$meta.get("staffQuestMode", null);
  const getRole = () => {
    const rawRole = getRoleInclProducer(root);
    if (rawRole !== "guest") return rawRole;
    if (!staffQuestMode) return null;
    return staffQuestMode === "full" ? "guest" : "observer";
  };
  const role = getRole();
  const availableSteps =
    variants && role
      ? root.$meta.find("accountOnboardingSteps", {
          status: "active",
          variants: {op: "overlaps", value: variants},
          roles: {op: "has", value: role},
          $order: ["chapter", "sortValue"],
        })
      : [];
  return {stepProgress: steps || {}, availableSteps, role};
};

export const completeOnboardingStep = async (stepKey: FrontendKey) => {
  if (!api.getRoot().loggedInUser) return;
  const {stepProgress, availableSteps} = await waitForResultPromise(() => {
    if (!isOnboardingAvailable()) {
      return {stepProgress: {}, availableSteps: [], role: null};
    } else {
      return getAvailableAccountOnboardingInfo(api.getRoot());
    }
  });
  if (stepProgress[stepKey]?.completedAt) return;
  const availableStepKeys = new Set(availableSteps.map((a) => a.key));
  if (!availableStepKeys.has(stepKey)) return;
  (api.mutate as any).onboarding.progress({step: stepKey, stepInfo: {completedAt: "now"}});
};

export const onboardingChapters = [
  {key: "basics1", label: "Basics 1", iconName: "PrioC"},
  {key: "basics2", label: "Basics 2", iconName: "PrioB"},
  {key: "basics3", label: "Basics 3", iconName: "PrioA"},
  {key: "hand", label: "Hand", iconName: "Hand"},
  {key: "textEditor", label: "Text Editor", iconName: "Doc"},
  {key: "lifeCycle", label: "Life Cycle", iconName: "Play"},
  {key: "advanced", label: "Advanced 1", iconName: "Search"},
  {key: "advanced2", label: "Advanced 2", iconName: "DeckInbox"},
  {key: "moreCardTypes", label: "More Card Types", iconName: "Crown"},
  {key: "journeys", label: "Journeys", iconName: "Journey"},
  {key: "sorting", label: "Sorting", iconName: "Deck"},
  {key: "producer", label: "Producer", iconName: "Properties"},
  {key: "discord", label: "Discord", iconName: "Discord"},
  {key: "openDecks", label: "Open Decks", iconName: "Globe"},
  {key: "bonus", label: "Bonus", iconName: "Bookmark"},
  {key: "timeTracking", label: "Time Tracking", iconName: "StopWatch"},
] satisfies {key: string; label: string; iconName: IconName}[];

const XP_PER_LEVEL = 500;

export const getCurrentLevel = (
  args: OnboardingInfo
): {level: number; levelStartXp: number; currXp: number; levelEndXp: number} => {
  const {availableSteps, stepProgress} = args;
  let currXp = 0;
  for (const step of availableSteps) {
    const p = stepProgress[step.key];
    if (p?.completedAt) currXp += step.xp;
  }
  const level = Math.floor(currXp / XP_PER_LEVEL) + 1;
  return {
    level,
    levelStartXp: (level - 1) * XP_PER_LEVEL,
    currXp,
    levelEndXp: level * XP_PER_LEVEL,
  };
};

export const toRomanNumeral = (num: number): string => {
  const romanNumerals = [
    {value: 10, numeral: "X"},
    {value: 9, numeral: "IX"},
    {value: 5, numeral: "V"},
    {value: 4, numeral: "IV"},
    {value: 1, numeral: "I"},
  ];

  let result = "";

  for (const numeral of romanNumerals) {
    while (num >= numeral.value) {
      result += numeral.numeral;
      num -= numeral.value;
    }
  }

  return result;
};

export const getOnboardingStepInfoIfLoaded = (root: any): OnboardingInfo | null => {
  if (!root.loggedInUser) return null;
  const {isLoaded, result: onboardingInfo} = checkIfPresent(() => {
    const info = getAvailableAccountOnboardingInfo(root);
    if (info) {
      getCurrentLevel(info);
      if (info.availableSteps.length) {
        const firstStep = info.availableSteps[0];
        firstStep.$meta.get("chapter");
        firstStep.$meta.get("title");
        firstStep.$meta.get("xp");
      }
    }
    return info;
  }, api);
  return isLoaded ? onboardingInfo : null;
};

const JoinCodecksDiscordFooter = ({completed}: {completed: boolean}) => {
  const handleClick = () => {
    void completeOnboardingStep("joinCodecksDiscord");
  };

  return (
    <Row align="center" sp="16px">
      {!completed && (
        <DSButton variant="tertiary" size="sm" onClick={handleClick}>
          Skip
        </DSButton>
      )}
      <DSButton
        size="md"
        className={dsStyles.ml.auto}
        href="https://discord.gg/yCAb26x"
        forwardProps={{onClick: handleClick}}
      >
        Join
      </DSButton>
    </Row>
  );
};

export const onboardingStepFooters: {[key: string]: FunctionComponent<{completed: boolean}>} = {
  joinCodecksDiscord: JoinCodecksDiscordFooter,
};
